import React from 'react';
import PropTypes from 'prop-types';
import { firstBy } from 'thenby';
import colors from 'config/colors';
import Button from 'components/Button';
import DropDownFilter from 'components/DropDownFilter';
import { getTimesheetColumnWidths, getTimesheetTableMinWidth, TIMESHEET_ACTION_BUTTON_WIDTH } from './timesheet-column-widths';
import './TimesheetsFilterBar.scss';


const styles = {
  timesheetsFilterBarContainer: {
    flex: '0 0 auto',
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    padding: '0 0 12px',
    boxSizing: 'border-box',
    borderBottom: '1px solid rgba(164, 194, 244, 0.2)',
    backgroundColor: colors.dagny.backgroundLight6,
  },
  timesheetsFilterBarInnerContainer: {
    width: '100%',
    display: 'grid',
  },
  timesheetFilterContainer: {
    boxSizing: 'border-box',
    minWidth: 0,
    padding: '0 6px',
  },
  resetButtonContainer: {
    alignSelf: 'end',
    backgroundColor: colors.dagny.backgroundLight6,
    boxSizing: 'border-box',
    padding: '0 12px',
    width: TIMESHEET_ACTION_BUTTON_WIDTH + 24,
    zIndex: 2,
  },
  resetButtonStyle: {
    height: 36,
    minWidth: TIMESHEET_ACTION_BUTTON_WIDTH,
    whiteSpace: 'nowrap',
    width: TIMESHEET_ACTION_BUTTON_WIDTH,
  },
};

const statusOptions = [
  { value: null, label: 'All' },
  { value: 'created', label: 'Awaiting Submission' },
  { value: 'pending-approval-overdue', label: 'Pending Approval (Overdue)' },
  { value: 'pending-approval', label: 'Pending Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'paid', label: 'Paid' },
  { value: 'locked', label: 'Locked' },
];

const archivedStatusOption = { value: 'archived', label: 'Archived' };

const convertToSelectArrayCandidates = (candidates) => {
  if (!candidates) return [];

  const selectArray = Object.values(candidates)
    .map(candidate => ({ value: candidate.candidateKey, label: candidate.name }))
    .sort(firstBy('label'));

  return [{ value: null, label: 'All' }, ...selectArray];
};

const convertToSelectArrayAdminGroups = (adminGroups) => {
  const selectArray = adminGroups.map(adminGroup => ({
    value: adminGroup.key,
    label: adminGroup.name,
  })).sort(firstBy('label'));
  return [{ value: null, label: 'All' }, ...selectArray];
};

const createSpecialityOptions = (specialities) => {
  const specialityOptions = specialities.map(speciality => ({ value: speciality.key, label: speciality.name }));
  return [{ value: null, label: 'All' }, ...specialityOptions];
};

const createSiteOptions = (sites) => {
  const siteOptions = sites.map(site => ({ value: site.key, label: site.name }));
  return [{ value: null, label: 'All' }, ...siteOptions];
};

const createServiceOptions = (services) => {
  const serviceOptions = services.map(service => ({ value: service.key, label: service.name }));
  return [{ value: null, label: 'All' }, ...serviceOptions];
};

const customSelectBoxStyles = {
  valueContainer: { fontSize: '14px' },
  menuList: { boxSizing: 'border-box', width: '100%' },
  menu: { boxSizing: 'border-box', minWidth: '100%', width: '100%' },
  option: { fontSize: '14px' },
};

const TimesheetsFilterBar = (props) => {
  const availableStatusOptions = props.includeArchived ? [...statusOptions, archivedStatusOption] : statusOptions;
  const columnWidths = getTimesheetColumnWidths(props);
  const hasActiveFilters = [
    props.timesheetFilters.adminGroup,
    props.timesheetFilters.site,
    props.timesheetFilters.service,
    props.timesheetFilters.speciality,
    props.timesheetFilters.candidate,
    props.timesheetFilters.status,
  ].some(Boolean);
  const innerContainerStyle = {
    ...styles.timesheetsFilterBarInnerContainer,
    gridTemplateColumns: columnWidths.map(width => `minmax(0, ${width}fr)`).join(' '),
    minWidth: getTimesheetTableMinWidth(props),
  };

  return (
    <div style={styles.timesheetsFilterBarContainer}>
      <div style={innerContainerStyle}>
        <div aria-hidden="true" />
        {props.adminGroupsFeatureFlag && (
          <div style={styles.timesheetFilterContainer}>
            <DropDownFilter
              title="Care Group:"
              placeholder="Filter by Care Group..."
              options={props.orgAdminGroups.length ?
                convertToSelectArrayAdminGroups(props.orgAdminGroups)
                : []}
              value={convertToSelectArrayAdminGroups(props.orgAdminGroups).find(option => option.value === props.timesheetFilters.adminGroup)}
              onChange={obj => props.setTimesheetsFilterValue('adminGroup', obj.value)}
              customStyles={customSelectBoxStyles}
              width="100%"
            />
          </div>
        )}
        <div style={styles.timesheetFilterContainer}>
          <DropDownFilter
            title="Site"
            placeholder="Filter by Site..."
            options={createSiteOptions(props.adminSites)}
            value={createSiteOptions(props.adminSites).find(option => option.value === props.timesheetFilters.site)}
            onChange={obj => props.setTimesheetsFilterValue('site', obj.value)}
            customStyles={customSelectBoxStyles}
            width="100%"
          />
        </div>
        {props.servicesFeatureFlag && (
          <div style={styles.timesheetFilterContainer}>
            <DropDownFilter
              title="Service"
              placeholder="Filter by Service..."
              options={createServiceOptions(props.services)}
              value={createServiceOptions(props.services).find(option => option.value === props.timesheetFilters.service)}
              onChange={obj => props.setTimesheetsFilterValue('service', obj.value)}
              customStyles={customSelectBoxStyles}
              width="100%"
            />
          </div>
        )}
        <div style={styles.timesheetFilterContainer}>
          <DropDownFilter
            title="Speciality:"
            placeholder="Filter by Speciality..."
            options={createSpecialityOptions(props.orgSpecialities)}
            value={createSpecialityOptions(props.orgSpecialities).find(option => option.value === props.timesheetFilters.speciality)}
            onChange={obj => props.setTimesheetsFilterValue('speciality', obj.value)}
            customStyles={customSelectBoxStyles}
            width="100%"
          />
        </div>
        <div style={styles.timesheetFilterContainer}>
          <DropDownFilter
            title="Candidate:"
            options={convertToSelectArrayCandidates(props.candidates)}
            placeholder="Filter by candidate..."
            value={convertToSelectArrayCandidates(props.candidates).find(option => option.value === props.timesheetFilters.candidate)}
            onChange={obj => props.setTimesheetsFilterValue('candidate', obj.value)}
            loading={props.isFetchingCandidates}
            customStyles={customSelectBoxStyles}
            width="100%"
          />
        </div>
        <div style={styles.timesheetFilterContainer}>
          <DropDownFilter
            title="Timesheet Status:"
            placeholder="Filter by timesheet status..."
            options={availableStatusOptions}
            value={availableStatusOptions.find(option => option.value === props.timesheetFilters.status)}
            onChange={obj => props.setTimesheetsFilterValue('status', obj.value)}
            customStyles={customSelectBoxStyles}
            width="100%"
          />
        </div>
        {hasActiveFilters && (
          <div className="timesheets-filter-bar__reset" style={styles.resetButtonContainer}>
            <Button
              shadow={false}
              style={styles.resetButtonStyle}
              white
              onClick={props.resetTimesheetFilters}
            >
              Reset Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const { arrayOf, object, func, objectOf, bool, shape, string, number } = PropTypes;

TimesheetsFilterBar.propTypes = {
  includeArchived: bool,
  orgAdminGroups: arrayOf(object).isRequired,
  candidates: objectOf(object).isRequired,
  orgSpecialities: arrayOf(shape({ key: string, name: string })).isRequired,
  services: arrayOf(shape({ key: string, name: string })).isRequired,
  isFetchingCandidates: bool,
  resetTimesheetFilters: func.isRequired,
  setTimesheetsFilterValue: func.isRequired,
  timesheetFilters: shape({
    status: string,
    candidate: string,
    overdue: number.isRequired,
    adminGroup: string,
    service: string,
  }).isRequired,
  adminSites: arrayOf(shape({
    key: string,
    name: string,
    abbr: string,
    orgKey: string,
    timezone: string,
    orgName: string,
    canManageShifts: bool,
    enabled: bool,
  })).isRequired,
  adminGroupsFeatureFlag: bool.isRequired,
  servicesFeatureFlag: bool.isRequired,
};

TimesheetsFilterBar.defaultProps = {
  includeArchived: false,
  isFetchingCandidates: false,
};
export default TimesheetsFilterBar;
